<template>
    <div
        ref="rootEl"
        class="mp-dock-panel"
        :class="panelClasses"
        :data-panel-id="panelId"
        :data-node-id="nodeId"
        :data-dock-host-id="dockHostId"
        :data-dock-host-type="dockHostType"
        :data-dock-panel-id="dockPanelId"
        :data-dock-host-label="dockHostLabel"
        tabindex="-1"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
    >
        <DockPanelChrome
            v-if="showChrome && !collapsed"
            :panel-id="panelId"
            :title="meta?.title"
            :icon="meta?.icon"
            :pinned="isPinned"
            :closable="meta?.closable !== false"
            mode="panel"
            @hide="$emit('hide', panelId)"
            @toggle-pin="$emit('toggle-pin', panelId)"
            @drag-start="(payload) => $emit('drag-start', payload)"
        />
        <QBar
            v-else-if="collapsed"
            dense
            class="mp-dock-panel-header bg-grey-3 text-dark cursor-pointer"
            @click="$emit('expand', panelId)"
        >
            <QIcon v-if="meta?.icon" :name="meta.icon" size="xs" class="q-mr-xs" />
            <span class="ellipsis">{{ meta?.title || panelId }}</span>
        </QBar>
        <div v-show="!collapsed" class="mp-dock-panel-body">
            <div :id="teleportTargetId" ref="contentTarget" class="mp-dock-panel-host" />
        </div>
    </div>
</template>

<script>
import { QBar, QIcon } from 'quasar';
import DockPanelChrome from './DockPanelChrome.vue';
import { PANEL_KIND_TOOL, UNPIN_COLLAPSE_DELAY_MS } from './constants.js';

export default {
    name: 'DockPanelLeaf',
    components: { DockPanelChrome, QBar, QIcon },

    props: {
        panelId: { type: String, required: true },
        nodeId: { type: String, required: true },
        meta: { type: Object, default: () => ({}) },
        panelKind: { type: String, default: PANEL_KIND_TOOL },
        collapsed: { type: Boolean, default: false },
        isPinned: { type: Boolean, default: true },
        excludeDockHost: { type: Boolean, default: false },
        excludeChrome: { type: Boolean, default: false },
    },

    emits: ['hide', 'toggle-pin', 'drag-start', 'expand', 'focus-change'],

    inject: {
        multipanelDockLayout: { default: null },
    },

    data() {
        return {
            _collapseTimer: null,
        };
    },

    computed: {
        panelClasses() {
            return {
                'mp-dock-panel--collapsed': this.collapsed,
                'mp-dock-panel--tool': this.panelKind === PANEL_KIND_TOOL,
            };
        },
        showChrome() {
            return !this.excludeChrome;
        },
        dockHostId() {
            return this.excludeDockHost ? undefined : this.nodeId;
        },
        dockHostType() {
            return this.excludeDockHost ? undefined : 'panel';
        },
        dockPanelId() {
            return this.excludeDockHost ? undefined : this.panelId;
        },
        dockHostLabel() {
            return this.excludeDockHost ? undefined : this.meta?.title || this.panelId;
        },
        teleportTargetId() {
            return `mp-dock-panel-${this.panelId}`;
        },
    },

    watch: {
        panelId(newId, oldId) {
            if (oldId && oldId !== newId) {
                this.multipanelDockLayout?.unmountPanelFromTarget?.(oldId);
            }
            this.$nextTick(() => this.mountPanelContent());
        },
        collapsed(val) {
            if (!val) this.$nextTick(() => this.mountPanelContent());
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.mountPanelContent();
            this.multipanelDockLayout?.setPanelHostEl?.(
                this.panelId,
                this.$refs.rootEl,
            );
        });
    },

    updated() {
        this.$nextTick(() => this.mountPanelContent());
    },

    beforeUnmount() {
        if (this._collapseTimer) clearTimeout(this._collapseTimer);
        this.multipanelDockLayout?.setPanelHostEl?.(this.panelId, null);
        this.multipanelDockLayout?.unmountPanelFromTarget?.(this.panelId);
    },

    methods: {
        mountPanelContent() {
            if (this.collapsed) return;
            const target = this.$refs.contentTarget;
            if (!target || !this.multipanelDockLayout) return;
            this.multipanelDockLayout.mountPanelToTarget(this.panelId, target);
        },

        onFocusIn() {
            if (this._collapseTimer) clearTimeout(this._collapseTimer);
            this.$emit('focus-change', { panelId: this.panelId, focused: true });
        },

        onFocusOut(e) {
            if (this.isPinned) return;
            const root = this.$refs.rootEl;
            if (root?.contains(e.relatedTarget)) return;
            this._collapseTimer = setTimeout(() => {
                if (!this.$refs.rootEl?.contains(document.activeElement)) {
                    this.$emit('focus-change', {
                        panelId: this.panelId,
                        focused: false,
                    });
                }
            }, UNPIN_COLLAPSE_DELAY_MS);
        },
    },
};
</script>
